export type FeedbackCategory =
  | 'performance'
  | 'error'
  | 'compatibility'
  | 'usability'
  | 'feature-request'
  | 'copy-design'
  | 'data-error'
  | 'safety-improvement';

export type FeedbackDraft = {
  category: FeedbackCategory;
  description: string;
  diagnosticsConsent: boolean;
  screenshotConsent: boolean;
  screenshotName: string;
};

export type FeedbackTimelineItem = {
  id: string;
  status: 'received' | 'analyzing' | 'awaiting-approval' | 'released';
  label: string;
  detail: string;
  at: string;
};

export type FeedbackSubmission = {
  feedbackId: string;
  report: {
    category: FeedbackCategory;
    description: string;
    route: string;
    occurredAt: string;
    clientContext: null | Record<string, unknown>;
  };
};

export const initialFeedbackDraft: FeedbackDraft = {
  category: 'usability',
  description: '',
  diagnosticsConsent: true,
  screenshotConsent: false,
  screenshotName: '',
};

export function buildFeedbackSubmission(
  draft: FeedbackDraft,
  input: {
    route: string;
    now: string;
    viewport: { width: number; height: number };
    online: boolean;
    screenshotEvidence?: { manifestId: string; evidenceSha256: string } | null;
  },
): FeedbackSubmission {
  if (!draft.description.trim()) throw new Error('불편한 내용을 입력해 주세요.');
  if (!draft.diagnosticsConsent) {
    return {
      feedbackId: `feedback-${input.now.replace(/\D/gu, '').slice(0, 14)}`,
      report: {
        category: draft.category,
        description: draft.description.trim(),
        route: input.route,
        occurredAt: input.now,
        clientContext: null,
      },
    };
  }
  return {
    feedbackId: `feedback-${input.now.replace(/\D/gu, '').slice(0, 14)}`,
    report: {
      category: draft.category,
      description: draft.description.trim(),
      route: input.route,
      occurredAt: input.now,
      clientContext: {
        consent: { diagnostics: true, screenshot: draft.screenshotConsent },
        appVersion: '0.1.0',
        viewportWidth: input.viewport.width,
        viewportHeight: input.viewport.height,
        browser: null,
        operatingSystem: null,
        deviceClass:
          input.viewport.width <= 480
            ? 'phone'
            : input.viewport.width <= 900
              ? 'tablet'
              : 'desktop',
        networkState: input.online ? 'online' : 'offline',
        loadDurationMs: null,
        errorTraceId: null,
        requestTraceIds: [],
        persona: null,
        siteId: 'eumseong',
        screenshotManifestId:
          draft.screenshotConsent && input.screenshotEvidence
            ? input.screenshotEvidence.manifestId
            : null,
        screenshotEvidenceSha256:
          draft.screenshotConsent && input.screenshotEvidence
            ? input.screenshotEvidence.evidenceSha256
            : null,
      },
    },
  };
}

const STORAGE_KEY = 'kop-eumseong-feedback-timeline-v1';

export function readFeedbackTimeline(storage: Pick<Storage, 'getItem'>): FeedbackTimelineItem[] {
  try {
    const parsed = JSON.parse(storage.getItem(STORAGE_KEY) ?? '[]') as unknown;
    return Array.isArray(parsed) ? (parsed as FeedbackTimelineItem[]) : [];
  } catch {
    return [];
  }
}

export function appendFeedbackTimeline(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  item: FeedbackTimelineItem,
): FeedbackTimelineItem[] {
  const next = [item, ...readFeedbackTimeline(storage)].slice(0, 20);
  storage.setItem(STORAGE_KEY, JSON.stringify(next));
  return next;
}
